import React from 'react';

import { getRankDifferenceDisplay } from './utils';
import Icon from '@components/atoms/icon';

const STAT_LABEL_CLASS =
  'text-[10px] md:text-[11px] font-semibold tracking-[0.08em] uppercase text-neutral-500';

const STAT_VALUE_CLASS =
  'mt-1 text-lg md:text-2xl font-bold tabular-nums text-neutral-900 leading-none';

const CELL_CLASS =
  'flex flex-col items-center md:items-start bg-white px-4 py-3 md:px-5 md:py-4';

type LiveRankData = {
  points: number;
  hits: number;
  predictedRank?: number | null;
  previousRank?: number | null;
};

type LiveScoreBoardProperties = {
  liveRankData: LiveRankData;
  currentGameweek: number | null;
};

export const LiveScoreBoard = ({
  liveRankData,
  currentGameweek
}: LiveScoreBoardProperties) => {
  const { points, hits, predictedRank, previousRank } = liveRankData;

  const hasRanks = predictedRank != null && previousRank != null;
  const isGreenArrow = hasRanks ? predictedRank < previousRank : false;
  const rankDifference = hasRanks ? previousRank - predictedRank : null;
  const isPreSeason = predictedRank == null || currentGameweek === 1;
  const isFlatRank = hasRanks && predictedRank === previousRank;

  const grossPoints = points + hits;
  const hasHits = hits > 0;

  const rankTrendColor =
    isPreSeason || isFlatRank
      ? 'text-neutral-900'
      : isGreenArrow
      ? 'text-success-600'
      : 'text-danger-600';

  const rankChangeColor =
    isPreSeason || isFlatRank
      ? 'bg-neutral-100 text-neutral-700'
      : isGreenArrow
      ? 'bg-success-100 text-success-700'
      : 'bg-danger-100 text-danger-700';

  return (
    <div
      className="mt-2 md:mt-3 overflow-hidden rounded-2xl bg-neutral-200 ring-1 ring-neutral-200 shadow-sm shadow-neutral-300/40"
      data-cy="live-scoreboard"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px">
        <div className={CELL_CLASS}>
          <span className={STAT_LABEL_CLASS}>GW points</span>
          <span
            className={`${STAT_VALUE_CLASS} flex items-baseline gap-1.5`}
            data-cy="live-gw-points"
          >
            <span>{points}</span>
            {hasHits && (
              <span className="text-xs md:text-sm font-medium text-neutral-400">
                {grossPoints}
                <span className="text-danger-600"> -{hits}</span>
              </span>
            )}
          </span>
        </div>

        <div className={CELL_CLASS}>
          <span className={STAT_LABEL_CLASS}>Live rank</span>
          <span
            className={`${STAT_VALUE_CLASS} flex items-center gap-1.5 ${rankTrendColor}`}
            data-cy="live-live-rank-score"
          >
            <span>{predictedRank?.toLocaleString() ?? '-'}</span>
            {!isPreSeason && !isFlatRank && predictedRank != null && (
              <Icon
                name="ArrowUpRightFilled"
                aria-hidden="true"
                className={`w-4 h-4 md:w-5 md:h-5 ${
                  isGreenArrow ? '' : 'rotate-90'
                }`}
              />
            )}
          </span>
        </div>

        <div className={CELL_CLASS}>
          <span className={STAT_LABEL_CLASS}>Last GW rank</span>
          <span className={STAT_VALUE_CLASS} data-cy="live-last-gw-points">
            {previousRank?.toLocaleString() ?? '-'}
          </span>
        </div>

        <div className={CELL_CLASS}>
          <span className={STAT_LABEL_CLASS}>Rank change</span>
          <span
            className={`mt-1.5 inline-flex items-center rounded-full px-2.5 py-0.5 text-sm md:text-base font-bold tabular-nums leading-snug ${rankChangeColor}`}
            data-cy="live-points-rank-difference"
          >
            {isPreSeason
              ? '-'
              : isFlatRank
              ? '0'
              : getRankDifferenceDisplay(
                  predictedRank,
                  currentGameweek,
                  rankDifference,
                  isGreenArrow
                )}
          </span>
        </div>
      </div>
    </div>
  );
};
